import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Image as ImageIcon } from "lucide-react";
import Button from "../../components/common/Button";
import InputField from "../../components/common/InputField";
import MediaPickerModal from "../../components/common/MediaPickerModal";

const GlassCard = ({ children, className = "" }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const AdminCreateBlog = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ 
    title: "", 
    author: "Admin", 
    status: "Draft", 
    thumbnail: "", 
    content: "" 
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

  const handleSave = () => {
    setIsSaving(true);
    
    // Simulate API delay
    setTimeout(() => {
      const newPost = {
        ...formData,
        id: `POST-${Math.floor(Math.random() * 10000)}`,
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
        views: 0,
        thumbnail: formData.thumbnail || "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=150&auto=format&fit=crop"
      };

      // Retrieve existing posts from localStorage or use empty array
      const existingPostsStr = localStorage.getItem('blog_posts');
      let existingPosts = [];
      if (existingPostsStr) {
        existingPosts = JSON.parse(existingPostsStr);
      }
      
      // Save updated list
      localStorage.setItem('blog_posts', JSON.stringify([newPost, ...existingPosts]));
      
      navigate("/staff/dashboard/blog");
    }, 600);
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700 cht-max-w-4xl cht-mx-auto">
      <MediaPickerModal
        isOpen={isMediaPickerOpen}
        onClose={() => setIsMediaPickerOpen(false)}
        onSelect={(asset) => {
          const url = asset.url || asset.external_url || "";
          setFormData((prev) => ({ ...prev, thumbnail: url }));
        }}
        allowedTypes={["image"]}
      />

      <header className="cht-flex cht-items-center cht-justify-between cht-gap-4">
        <div className="cht-flex cht-items-center cht-gap-4">
          <button 
            onClick={() => navigate("/staff/dashboard/blog")}
            className="cht-p-3 cht-bg-white cht-rounded-2xl cht-text-gray-500 hover:cht-bg-gray-50 cht-shadow-sm cht-border cht-border-gray-100 cht-transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">Create New Blog Post</h1>
            <p className="cht-text-gray-500 cht-font-medium">Publish dynamic content directly to the student and public platforms.</p>
          </div>
        </div>
        <div className="cht-flex cht-gap-3">
          <Button variant="outline" onClick={() => navigate("/staff/dashboard/blog")}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Publishing..." : "Publish Post"}
          </Button>
        </div>
      </header>

      <GlassCard className="cht-rounded-[40px] cht-p-8">
        <div className="cht-space-y-8">
          <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-6">
            <InputField 
              label="Post Title" 
              placeholder="Enter an engaging title" 
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              leftIcon={<FileText size={18} />}
            />
            <InputField 
              label="Author Name" 
              placeholder="Author" 
              value={formData.author}
              onChange={(e) => setFormData({...formData, author: e.target.value})}
            />
          </div>

          <div className="cht-space-y-2">
            <div className="cht-flex cht-items-end cht-gap-3">
              <div className="cht-flex-1">
                <InputField 
                  label="Featured Image URL" 
                  placeholder="https://example.com/image.jpg" 
                  value={formData.thumbnail}
                  onChange={(e) => setFormData({...formData, thumbnail: e.target.value})}
                  leftIcon={<ImageIcon size={18} />}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsMediaPickerOpen(true)}
                className="cht-rounded-2xl !cht-py-3.5 cht-whitespace-nowrap cht-text-xs cht-font-bold cht-border-[#3e57da]/30 cht-text-[#3e57da] hover:cht-bg-[#f2f4fd]"
              >
                Select from Media Library
              </Button>
            </div>
            {formData.thumbnail && (
              <div className="cht-mt-2 cht-w-36 cht-h-24 cht-rounded-2xl cht-overflow-hidden cht-border cht-border-gray-200">
                <img
                  src={formData.thumbnail}
                  alt="Thumbnail Preview"
                  className="cht-w-full cht-h-full cht-object-cover"
                />
              </div>
            )}
          </div>
          
          <div className="cht-flex cht-flex-col cht-gap-2">
            <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-[0.2em] cht-ml-2">Article Content</label>
            <textarea 
              rows={12}
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              className="cht-w-full cht-bg-gray-50 cht-border-2 cht-border-transparent focus:cht-border-[#3e57da]/20 focus:cht-bg-white cht-rounded-[24px] cht-p-6 cht-text-sm cht-font-bold cht-text-[#1e293b] cht-outline-none cht-transition-all cht-resize-none"
              placeholder="Write your amazing content here..."
            />
          </div>

          <div className="cht-flex cht-flex-col cht-gap-2">
            <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-[0.2em] cht-ml-2">Publish Status</label>
            <div className="cht-flex cht-gap-4 cht-max-w-md">
              <button 
                onClick={() => setFormData({...formData, status: "Draft"})}
                className={`cht-flex-1 cht-py-4 cht-rounded-2xl cht-text-sm cht-font-black cht-transition-all ${formData.status === 'Draft' ? 'cht-bg-[#1e293b] cht-text-white cht-shadow-md' : 'cht-bg-gray-50 cht-text-gray-500 hover:cht-bg-gray-100'}`}
              >
                Save as Draft
              </button>
              <button 
                onClick={() => setFormData({...formData, status: "Published"})}
                className={`cht-flex-1 cht-py-4 cht-rounded-2xl cht-text-sm cht-font-black cht-transition-all ${formData.status === 'Published' ? 'cht-bg-emerald-500 cht-text-white cht-shadow-md' : 'cht-bg-gray-50 cht-text-gray-500 hover:cht-bg-gray-100'}`}
              >
                Publish Immediately
              </button>
            </div>
          </div>
        </div>
      </GlassCard>
    </div>
  );
};

export default AdminCreateBlog;
